import React from 'react'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { createContext } from 'lib/createContext'
import { usePositionByIdQuery } from 'queries/positions'

type PositionDetailContextState = {
  state: NonNullable<ReturnType<typeof usePositionByIdQuery>['data']> & {
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
  }
  actions: {
    refetch: ReturnType<typeof usePositionByIdQuery>['refetch']
  }
}

const [PositionDetailContext, usePositionDetailContextValue] =
  createContext<PositionDetailContextState>({
    name: 'PositionDetailContext',
  })

export const PositionDetailProvider = ({
  children,
  positionId,
}: {
  children: React.ReactNode
  positionId: number
}) => {
  const {
    data,
    isPending: isPositionDetailLoading,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = usePositionByIdQuery(positionId)

  if (isPositionDetailLoading) {
    return <Loading />
  }

  if (isError && !data) {
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value: PositionDetailContextState = {
    state: {
      ...data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
    },
    actions: {
      refetch,
    },
  }

  return (
    <PositionDetailContext.Provider value={value}>
      {children}
    </PositionDetailContext.Provider>
  )
}

export { usePositionDetailContextValue }
